import React, {
  useRef,
  useState,
} from 'react';
import {
  Form,
  Field,
  Formik,
} from 'formik';
import {
  withApi,
  withNotification,
  withPermissionsChecker,
} from 'wrappers';
import {
  messages,
  masterMessages,
} from 'constantes';
import {
  NumberBase,
  TextAreaBase,
  DropZoneBase,
  DatePickerBase,
  AutocompleteBase,
} from 'components';
import {
  Grid,
  Dialog,
  Tooltip,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import {
  CloudUpload,
  CloudDownload,
} from '@material-ui/icons';
import {
  ButtonSave,
  ButtonCancelar,
} from 'components/Controls';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { Delete } from 'react-feather';
import Table from 'components/Table/Table';
import endPoints from 'endPoints/endPoints';
import ApiServiceFecth from 'services/ApiServiceFecth';
import LabelLink from 'components/labelLink/labelLink';
import { fireDownloadFromResponse } from 'utils/helpers';
import ConfirmModal from 'components/dialogs/ConfirmModal';
import DefaultActions from 'components/Table/DefaultActions';

const labelLinkGasto = {
  gasto: '/app/configuracion/tesoreria/gastos/edit',
  gastoPermissions: 'app:general:configuracion:tesoreria:gastos:edit',
};

/**
 * @desc Objeto con validaciones del form
 */
const validationSchema = Yup.object().shape({
  fechaGasto: Yup.date().nullable()
    .required('El campo fecha del gasto es requerido'),
  valor: Yup.string()
    .required('El campo valor es requerido'),
  proveedor: Yup.string()
    .required('El campo proveedor es requerido'),
});

const GastoGenericTab = ({
  doPut,
  doGet,
  doPost,
  appInfo,
  appError,
  doDelete,
  entidadId,
  appSuccess,
  appWarning,
  abreviatura,
  edit = true,
  checkAccess,
  editable = true,
  genericException,
  permissions = {},
}) => {
  const child = useRef();

  const [deleteDocumentId, setDeleteDocumentId] = useState(null);
  const [openModalEdit, setOpenModalEdit] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [idDelete, setIdDelete] = useState(null);
  const [idToEdit, setIdToEdit] = useState(null);
  const [selected, setSelected] = useState([]);
  const file = useState('');

  const [state, setState] = useState({
      valor: '',
      proveedor: '',
      fechaGasto: null,
      justificacion: '',
      nombreProveedor: '',
  });

  const endPoint = endPoints.scm.compra.maestros.gastoEntidad;
  const m = masterMessages.scm.compra.maestros.gastoEntidad;

  const handleModalCreate = () => {
    setOpenModal(!openModal);
    setSelected([]);
  };

  const handleDeleteDocument = ({ id = null} = {}) => setDeleteDocumentId(id);

  const handleModalDelete = ({ id = null } = {}) => setIdDelete(id);

  const handleModalUpdate = ({ id = null } = {}) => setIdToEdit(id);

  const handleOpenModalUpdate =  async ({ id = null } = {}) => {
    setOpenModalEdit(id);

    if (id) {
      try {
        const {
          valor,
          proveedor,
          justificacion,
          fecha_gasto: fechaGasto,
          proveedor_id: proveedorId,
        } = await doGet({
          url: `${endPoint.base}/${id}`,
        });

        const {
          tercero: {
            nombrecompleto: nombreProveedor = '',
          } = {},
        } = proveedor || {};
  
        setState({
          ...state,
          valor: valor || '',
          proveedor: proveedorId || '',
          fechaGasto: fechaGasto || null,
          justificacion: justificacion || '',
          nombreProveedor: nombreProveedor || '',
        });
      } catch (e) {
        genericException(e);
      }
    }
  };
  
  const submitDocument = async (values) => {
    const { file } = values;
    const url = `${endPoint.base}/${idToEdit}`;
    const data = { file };
    try {
      await doPut({ url, data });
      appInfo(messages.crud.update);
      handleModalUpdate();
      child.current.refresh();
    } catch (e) {
      appError(messages.crud.fail, e);
    }
  };
  
  const download = async (row) => {
    try {
      const url = `${endPoint.base}/download/${row.id}`;
      const resp = await ApiServiceFecth.download(url);
      await fireDownloadFromResponse(resp, `${row.gasto}.${row.ext}`);
    } catch (e) {
      appError(messages.dataFetch.fail);
    }
  };
  
  const deleteDocument = async () => {
    const url = `${endPoint.base}/deletefile/${deleteDocumentId}`;
    try {
      await doDelete({ url });
      handleDeleteDocument();
      child.current.refresh();
      appWarning(messages.crud.delete);
    } catch (e) {
      appError(messages.crud.fail, e);
    }
  };

  const columns = [
    {
      name: 'gasto',
      label: 'Gasto',
      type: 'autocomplete',
      extra: {
        url: endPoints.scm.compra.maestros.gastos.autocomplete,
        requestParams: { filter: true },
      },
      component: row => (
        <LabelLink
          labelName={row.gasto}
          labelUrl={`${labelLinkGasto.gasto}/${row.gastoId}`}
          targetType="_blank"
          permission={[labelLinkGasto.gastoPermissions]}
        />
      ),
    },
    {
      name: 'clasificacion',
      label: 'Clasificación gasto',
      type: 'autocomplete',
      extra: {
        url: endPoints.scm.compra.maestros.clasificacionGasto.autocomplete,
      },
    },
    {
      name: 'tipo',
      label: 'Tipo',
    },
    {
      name: 'cuentaContable',
      label: 'cuenta',
      type: 'autocomplete',
      extra: {
        url: endPoints.contabilidad.maestros.cuentasContables.autoComplete,
        requestParams: { withName: true },
      },
    },
    {
      name: 'fecha_gasto',
      label: 'Fecha del gasto',
      type: 'date',
    },
    {
      name: 'proveedor',
      label: 'Proveedor',
      type: 'autocomplete',
      extra: {
        url: endPoints.scm.compra.maestros.proveedor.autocomplete,
        requestParams: { filter: true, withName: true },
      },
    },
    {
      name: 'costo',
      label: 'Costo',
      type: 'check',
      filterMessage: "Si desea buscar por costos activos, dar clic " +
      "para activar, para costos inactivos dar clic para activar y de nuevo para desactivar",
    },
    {
      name: 'gastoBool',
      label: 'Gastos',
      type: 'check',
      filterMessage: "Si desea buscar por gastos activos, dar clic " +
      "para activar, para gastos inactivos dar clic para activar y de nuevo para desactivar",
    },
    {
      name: 'adjunto',
      label: 'Adjunto',
      type: 'check',
      filterMessage: 'Si desea buscar por adjuntos cargados, dar clic '
      + 'para activar, para adjuntos sin cargar dar clic para activar y de nuevo para desactivar',
    },
    {
      name: 'acciones',
      label: 'Acciones',
      filter: false,
      component: row => (
        <DefaultActions
          row={row}
          onEdit={handleOpenModalUpdate}
          onDelete={handleModalDelete}
          disabledUpdate={!editable || !edit}
          disabledDelete={!editable || !edit}
          deletePermissions={permissions.deletePermissions}
        >
          <Tooltip
            title="Subir"
            component="div"
          >
            <IconButton
              onClick={() => handleModalUpdate({id: row.id})}
              disabled={
                !checkAccess(permissions.updatePermissions)
                || row.adjunto
                || !editable
              }
            >
              <CloudUpload />
            </IconButton>
          </Tooltip>
          <Tooltip
            title="Descargar"
            component="div"
          >
            <IconButton
              onClick={() => download(row)}
              disabled={
                !checkAccess(permissions.updatePermissions)
                || !row.adjunto
                || !editable
              }
            >
              <CloudDownload />
            </IconButton>
          </Tooltip>
          <Tooltip
            title="Eliminar documento"
            component="div"
          >
            <IconButton
              onClick={() => handleDeleteDocument(row)}
              disabled={
                !checkAccess(permissions.updatePermissions)
                || !row.adjunto
                || !editable
              }
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </DefaultActions>
      ),
    },
  ];

  const columnsModal = [
    {
      name: 'gasto',
      label: 'Gasto',
      type: 'autocomplete',
      extra: {
        url: endPoints.scm.compra.maestros.gastos.autocomplete,
        requestParams: { filter: true },
      },
    },
    {
      name: 'clasificacionGasto',
      label: 'Clasificación gasto',
      type: 'autocomplete',
      extra: {
        url: endPoints.scm.compra.maestros.clasificacionGasto.autocomplete,
      },
    },
    {
      name: 'tipo',
      label: 'tipo',
    },
    {
      name: 'cuenta',
      label: 'cuenta',
      type: 'autocomplete',
      extra: {
        url: endPoints.contabilidad.maestros.cuentasContables.autoComplete,
        requestParams: { withName: true },
      },
    },
  ];

  const onRowsSelect = (rows) => {
    const data = [];
    rows.map(item => data.push({
      gastoId: item.id,
    }));

    setSelected(data);
  };

  const deleteGasto = async () => {
    try {
      await doDelete({
        url: `${endPoint.base}/${idDelete}`,
      });
      appWarning(messages.crud.delete);
      child.current.refresh();
      handleModalDelete();
    } catch (e) {
      genericException(e);
    }
  };

  const onSubmit = async () => {
    try {
      await doPost({
        url: endPoint.base,
        data: {
          entidadId,
          abreviatura,
          gastos: selected,
        },
      });
      appSuccess(messages.crud.new);
      handleModalCreate();
      child.current.refresh();
    } catch (e) {
      genericException(e);
    }
  };

  const mapValues = (formValues) => {
    const {
      valor,
      proveedor,
      fechaGasto,
      justificacion,
    } = formValues;

    return {
      valor,
      justificacion,
      fecha_gasto: fechaGasto,
      proveedor_id: proveedor,
    }
  }

  const submitEdit = async (values) => {
    const url = `${endPoint.base}/${openModalEdit}`;
    const data = mapValues(values);
    try {
      await doPut({ url, data });
      appInfo(messages.crud.update);
      handleOpenModalUpdate();
      child.current.refresh();
    } catch (e) {
      appError(messages.crud.fail, e);
    }
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={Boolean(idToEdit)}
        onClose={() => handleModalUpdate()}
      >
        <DialogTitle>
          Adjuntar documento
        </DialogTitle>
        <DialogContent>
          <Formik
            enableReinitialize
            initialValues={{ file }}
            onSubmit={submitDocument}
          >
            {subProps => (
              <Form>
                <Grid container direction="row">
                  <Grid
                    item
                    xs={12}
                    md={12}
                    xl={12}
                  >
                    <Field
                      label="File"
                      name="file"
                      component={DropZoneBase}
                    />
                  </Grid>
                </Grid>
                <DialogActions>
                  <ButtonCancelar
                    onClick={() => handleModalUpdate()}
                  />
                  <ButtonSave
                    label="Guardar"
                    disabled={subProps.isSubmitting}
                  />
                  </DialogActions>
                </Form>
              )}
            </Formik>
        </DialogContent>
      </Dialog>
      <ConfirmModal
        open={Boolean(deleteDocumentId)}
        onCancel={handleDeleteDocument}
        onClose={handleDeleteDocument}
        onAccept={deleteDocument}
        message={m.deleteDocumentMessage}
        title={m.deleteDocumentTitle}
      />
      <ConfirmModal
        open={Boolean(idDelete)}
        onClose={handleModalDelete}
        title={m.deleteTitle}
        message={m.deleteMessage}
        onAccept={deleteGasto}
      />
      {Boolean(openModalEdit) && (
        <Formik
          onSubmit={submitEdit}
          enableReinitialize
          initialValues={state}
          validationSchema={validationSchema}
        >
          {subProps => (
            <Dialog
              fullWidth
              maxWidth="lg"
              open={Boolean(openModalEdit)}
              onClose={handleOpenModalUpdate}
            >
              <DialogTitle>{m.updateTitle}</DialogTitle>
              <Form>
                <DialogContent>
                  <Grid
                    container
                    spacing={3}
                    direction="row"
                    justify="space-between"
                  >
                    <Grid
                      item
                      xs={12}
                      md={6}
                      xl={6}
                    >
                      <Field
                        label="Fecha del gasto"
                        name="fechaGasto"
                        component={DatePickerBase}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      xl={6}
                    >
                      <Field
                        label="Valor"
                        name="valor"
                        component={NumberBase}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={12}
                      xl={12}
                    >
                      <Field
                        name="proveedor"
                        label="Proveedor"
                        component={AutocompleteBase}
                        searchValue={state.nombreProveedor}
                        url={endPoints.scm.compra.maestros.proveedor.autocomplete}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={12}
                      xl={12}
                    >
                      <Field
                        label="Justificación"
                        name="justificacion"
                        component={TextAreaBase}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <ButtonCancelar
                    onClick={() => handleOpenModalUpdate()}
                  />
                  <ButtonSave
                    label="Guardar"
                    disabled={subProps.isSubmitting}
                  />
                </DialogActions>
              </Form>
            </Dialog>

          )}
        </Formik>
      )}
      <Dialog
        open={openModal}
        onClose={handleModalCreate}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {m.createTitle}
        </DialogTitle>
        <DialogContent>
          <Table
            serverSideUrl={endPoint.getGastosNotInEntidad}
            serverSideData={{
              entidadId,
              abreviatura,
            }}
            columns={columnsModal}
            onRowsSelect={onRowsSelect}
            genericSearch={false}
          />
        </DialogContent>
        <DialogActions>
          <ButtonCancelar onClick={handleModalCreate} />
          <ButtonSave
            label="Guardar"
            disabled={selected.length === 0}
            onClick={onSubmit}
          />
        </DialogActions>
      </Dialog>
      <Table
        forwardedRef={child}
        serverSideUrl={endPoint.base}
        serverSideData={{
          entidadId,
          abreviatura,
        }}
        columns={columns}
        onCreate={handleModalCreate}
        disableCreateButton={!editable || !edit}
        createPermissions={permissions.createPermissions}
      />
    </>
  );
};

GastoGenericTab.propTypes = {
  edit: PropTypes.bool,
  doPut: PropTypes.func,
  doGet: PropTypes.func,
  doPost: PropTypes.func,
  appInfo: PropTypes.func,
  editable: PropTypes.bool,
  appError: PropTypes.func,
  doDelete: PropTypes.func,
  appSuccess: PropTypes.func,
  appWarning: PropTypes.func,
  checkAccess: PropTypes.func,
  genericException: PropTypes.func,
  abreviatura: PropTypes.string.isRequired,
  permissions: PropTypes.oneOfType([
    PropTypes.object
  ]),
  entidadId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
};

export default withPermissionsChecker(withApi(withNotification(GastoGenericTab)));
